import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Editor } from 'primereact/editor';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { PickList } from 'primereact/picklist';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useEffect, useState } from 'react';

import { useEmployee } from '../../../context/employee';
import { useSite } from '../../../context/site';
import { GetEditorFormats } from '../../../shared';
import EmployeeDropdown from '../../employee-dropdown';
import ProgramSearch from '../../program/search';
import ServiceTypeIndividualDropdown from '../../service-type-ind-dropdown';

function ServiceIndividualCreate({ open, onClose, data }) {
    const { employee } = useEmployee();
    const { CreateServiceIndividual, IsServiceTypeJ2TC, SearchParents } = useSite();

    const [dateService, setDateService] = useState(new Date());
    const [employeeId, setEmployeeId] = useState('');
    const [parent, setParent] = useState('');
    const [filteredParents, setFilteredParents] = useState([]);
    const [serviceType, setServiceType] = useState('');
    const [sourceChildrenPrograms, setSourceChildrenPrograms] = useState([]);
    const [childrenPrograms, setChildrenPrograms] = useState([]);
    const [familyNumber, setFamilyNumber] = useState(0);
    const [notes, setNotes] = useState('');

    const [processing, setProcessing] = useState(false);
    const [processingError, setProcessingError] = useState('');

    const [openSearchProgram, setOpenSearchProgram] = useState(false);
    const [parentIdLoaded, setParentIdLoaded] = useState(false);

    const buttonDisabled = () => {
        if (!dateService) {
            return true;
        }

        if (!employeeId) {
            return true;
        }

        if (!parent) {
            return true;
        }

        if (!serviceType) {
            return true;
        }

        if (IsServiceTypeJ2TC(serviceType) && familyNumber <= 0) {
            return true;
        }

        return false;
    };

    const onCancel = () => {
        onClose({ status: 499, message: 'Canceled' });
    };

    const onCreate = () => {
        setProcessing(true);

        const createData = {
            dateService,
            employee: employeeId,
            parent: parent._id,
            serviceType,
            childrenPrograms: childrenPrograms.map((cp) => {
                const res = {
                    child: cp.child._id
                };

                if (cp.program?._id) {
                    res.program = cp.program._id;
                }

                return res;
            }),
            familyNumber,
            notes
        };

        CreateServiceIndividual(createData, (res) => {
            if (res.status !== 200) {
                setProcessingError(res.message);
                setProcessing(false);
            } else {
                onClose(res);
            }
        });
    };

    const onChange = (event) => {
        setSourceChildrenPrograms(event.source);
        setChildrenPrograms(event.target);
    };

    const onSearchParent = (data) => {
        SearchParents({ query: data.query }, (res) => {
            setFilteredParents(res.data.parents);
        });
    };

    const itemTemplate = (item) => {
        return (
            <div className="flex align-items-center">
                <div>
                    {item.nameFirst} {item.nameLast}, {item.city}, {item.state.itemData.abbr}
                </div>
            </div>
        );
    };

    const FooterContent = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onCancel}
                className="p-button-text"
            />
            <Button
                label="Create"
                disabled={buttonDisabled()}
                icon="pi pi-check"
                onClick={onCreate}
            />
        </div>
    );

    const sourceItemTemplate = (item) => {
        return (
            <div className="flex align-items-center">
                <div>
                    {item.child.nameFirst} {item.child.nameLast}
                </div>
            </div>
        );
    };

    const targetItemTemplate = (item) => {
        return (
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    {item.child.nameFirst} {item.child.nameLast}
                </div>

                {!item.program?._id ? (
                    <div className="flex-auto">
                        <Button
                            rounded
                            size="small"
                            icon="pi pi-plus"
                            severity="help"
                            onClick={() => {
                                setOpenSearchProgram(true);
                            }}
                        />
                    </div>
                ) : (
                    <>
                        <div className="flex-auto">{item.program.programName}</div>

                        <div className="flex-auto">
                            <Button
                                className="ml-1"
                                rounded
                                icon="pi pi-minus"
                                severity="danger"
                                size="small"
                                onClick={() => {
                                    setChildrenPrograms((prev) => {
                                        return [
                                            ...prev.map((c) => {
                                                if (c.child._id === item.child._id) {
                                                    return {
                                                        child: { ...c.child },
                                                        program: ''
                                                    };
                                                }

                                                return c;
                                            })
                                        ];
                                    });
                                }}
                            />
                        </div>
                    </>
                )}

                {!openSearchProgram ? null : (
                    <ProgramSearch
                        open={openSearchProgram}
                        onClose={(res) => {
                            setOpenSearchProgram(false);

                            if (res?._id) {
                                setChildrenPrograms((prev) => {
                                    return [
                                        ...prev.map((c) => {
                                            if (c.child._id === item.child._id) {
                                                return {
                                                    child: { ...c.child },
                                                    program: { ...res }
                                                };
                                            }

                                            return c;
                                        })
                                    ];
                                });
                            }
                        }}
                    />
                )}
            </div>
        );
    };

    // Set Employee to current Employee
    // Set source children
    useEffect(() => {
        setEmployeeId(employee?._id ? employee._id : '');
    }, [employee]);

    // Set parent if sent
    useEffect(() => {
        if (parentIdLoaded) {
            return;
        }

        if (!data) {
            setParentIdLoaded(true);
            return;
        }
        setParent(data);

        if (Array.isArray(data?.children)) {
            setSourceChildrenPrograms(
                data.children.map((c) => {
                    return {
                        child: { ...c },
                        program: ''
                    };
                })
            );
        } else {
            setSourceChildrenPrograms([]);
        }

        setChildrenPrograms([]);
        setParentIdLoaded(true);
    }, [data, parentIdLoaded]);

    return (
        <Dialog
            header="Create New Individual Service"
            visible={open}
            onHide={onCancel}
            footer={FooterContent}
            className="w-9"
        >
            <ErrorMessage
                error={processingError}
                onClick={() => {
                    setProcessingError('');
                }}
            />

            <ProcessingMessage processing={processing} />

            {!parentIdLoaded ? (
                <>
                    <ProgressSpinner />
                    <br />
                    <p>Checking for parent</p>
                </>
            ) : (
                <div className="mt-2" style={{ display: processing ? 'none' : 'block' }}>
                    <div className="card flex flex-wrap gap-3 p-fluid">
                        <div className="flex-auto">
                            <label htmlFor="dateServiceId" className="font-bold block mb-2">
                                Service Date
                            </label>

                            <Calendar
                                inputId="dateServiceId"
                                value={dateService}
                                onChange={(e) => setDateService(e.value)}
                                showIcon
                                showButtonBar
                                appendTo="self"
                            />
                        </div>

                        <div className="flex-auto">
                            <label htmlFor="employeeId" className="font-bold block mb-2">
                                Employee
                            </label>

                            <EmployeeDropdown
                                employeeId={employeeId}
                                onChange={(employeeId) => setEmployeeId(employeeId)}
                            />
                        </div>
                    </div>

                    <div className="card flex flex-wrap gap-3 p-fluid">
                        <div className="flex-auto">
                            <label htmlFor="serviceTypeId" className="font-bold block mb-2">
                                Service Type
                            </label>

                            <ServiceTypeIndividualDropdown
                                serviceTypeIndividualId={serviceType}
                                onChange={(serviceTypeId) => {
                                    setServiceType(serviceTypeId);
                                }}
                            />
                        </div>
                    </div>

                    {!IsServiceTypeJ2TC(serviceType) ? null : (
                        <div className="card flex flex-wrap gap-3 p-fluid">
                            <div className="flex-auto">
                                <label htmlFor="familyNumberId" className="font-bold block mb-2">
                                    Family Number
                                </label>

                                <InputText
                                    id="familyNumberId"
                                    value={familyNumber}
                                    keyfilter={/^[0-9]*$/}
                                    maxLength={5}
                                    onChange={(e) => {
                                        setFamilyNumber(e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="card flex flex-wrap gap-3 p-fluid">
                        <div className="flex-auto">
                            <label htmlFor="parentId" className="font-bold block mb-2">
                                Parent
                            </label>

                            <AutoComplete
                                field="nameFilter"
                                value={parent}
                                suggestions={filteredParents}
                                completeMethod={onSearchParent}
                                onSelect={(e) => {
                                    if (Array.isArray(e.value.children)) {
                                        setSourceChildrenPrograms(
                                            e.value.children.map((c) => {
                                                return {
                                                    child: { ...c },
                                                    program: ''
                                                };
                                            })
                                        );
                                        setChildrenPrograms([]);
                                    }
                                }}
                                onChange={(e) => {
                                    setParent(e.value);
                                }}
                                itemTemplate={itemTemplate}
                            />
                        </div>
                    </div>

                    {!parent?._id ? null : (
                        <div className="card flex flex-wrap gap-3 p-fluid">
                            <div className="flex-auto">
                                <label htmlFor="parentId" className="font-bold block mb-2">
                                    Children
                                </label>

                                <PickList
                                    source={sourceChildrenPrograms}
                                    target={childrenPrograms}
                                    onChange={onChange}
                                    sourceItemTemplate={sourceItemTemplate}
                                    showSourceControls={false}
                                    targetItemTemplate={targetItemTemplate}
                                    showTargetControls={false}
                                    sourceHeader={`Children for ${parent.nameFilter}`}
                                    targetHeader={`Selected children for ${parent.nameFilter}`}
                                    // sourceStyle={{ height: '30rem' }}
                                    // targetStyle={{ height: '30rem' }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="card flex flex-wrap gap-3 p-fluid">
                        <div className="flex-auto">
                            <label htmlFor="notesId" className="font-bold block mb-2">
                                Notes
                            </label>

                            <Editor
                                id="notesId"
                                headerTemplate={<GetEditorFormats />}
                                value={notes}
                                onTextChange={(e) => setNotes(e.htmlValue)}
                                style={{ height: '320px' }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
}

function ErrorMessage({ error, onClick }) {
    if (!error) {
        return null;
    }

    return <Message severity="error" content={error} onClick={onClick} />;
}

function ProcessingMessage({ processing }) {
    if (!processing) {
        return null;
    }

    return (
        <>
            <p>Creating individual service...</p>

            <ProgressSpinner />
        </>
    );
}

export default ServiceIndividualCreate;
